import React, { useState, useEffect, useRef } from 'react';
import {
    ArrowLeft,
    Download,
    RefreshCw,
    Sparkles,
    Image as ImageIcon,
    Video as VideoIcon,
    Loader2,
    Save,
    FileText,
    Clock,
    Play,
    File,
    ChevronRight,
    Sliders,
    Copy,
    LayoutDashboard,
    Folder,
    Wand2,
    HelpCircle,
    Search,
    Settings,
    Bolt,
    MoreHorizontal,
    Trash2,
    Coins,
    AlertCircle
} from 'lucide-react';
import { aiService } from '../services/aiService';
import CoinDisplay from './CoinDisplay';
import '../StudioLayout.css';

const RewriteView = ({
    originalData,
    videoName,
    initialData,
    rewrites,
    activeId,
    onSave,
    onEdit,
    onDelete,

    onNewClick,
    onNavigate
}) => {
    const [title, setTitle] = useState(videoName || "Untitled Script");
    const [editedScript, setEditedScript] = useState('');
    const [cachedTimestampedScript, setCachedTimestampedScript] = useState('');
    const [imagePrompt, setImagePrompt] = useState(initialData?.imagePrompt || '');
    const [videoPrompt, setVideoPrompt] = useState(initialData?.videoPrompt || '');
    const [isRewriting, setIsRewriting] = useState(false);
    const [showSaveToast, setShowSaveToast] = useState(false);
    const [selectedStyle, setSelectedStyle] = useState('Aggressive & Energetic');
    const [targetWordCount, setTargetWordCount] = useState(150);

    const videoRef = useRef(null);



    // Update state when initialData or originalData changes
    useEffect(() => {
        if (initialData) {
            setTitle(initialData.name || "Untitled Script");
            setEditedScript(initialData.script || '');
            setCachedTimestampedScript(initialData.timestampedOriginal || initialData.script || '');
            setImagePrompt(initialData.imagePrompt || '');
            setVideoPrompt(initialData.videoPrompt || '');
        } else if (originalData) {
            setTitle(videoName || "Untitled Script");
            const rawText = originalData.timestampedText || originalData.text;
            setCachedTimestampedScript(rawText);
            setEditedScript(rawText);
            setImagePrompt('');
            setVideoPrompt('');
        }
    }, [initialData, originalData, videoName]);

    const handleRewrite = async (style) => {
        setIsRewriting(true);
        setSelectedStyle(style);
        try {
            const cleanSource = originalData?.text || "";
            const result = await aiService.rewriteScript(cleanSource, style, true);

            // Handle both old (string) and new (object) response formats
            const newScript = typeof result === 'object' ? result.script : result;
            setEditedScript(newScript);
            setCachedTimestampedScript(newScript);

            // Generate prompts - now included in rewriteScript response from Cloud Function
            if (typeof result === 'object' && result.imagePrompt) {
                setImagePrompt(result.imagePrompt);
                setVideoPrompt(result.videoPrompt);
            } else {
                const prompts = await aiService.generatePrompts(newScript);
                setImagePrompt(prompts.imagePrompt);
                setVideoPrompt(prompts.videoPrompt);
            }
        } catch (error) {
            // Show user-friendly error from Cloud Function
            alert(error.message || "Rewrite gagal. Sila cuba lagi.");
        } finally {
            setIsRewriting(false);
        }
    };

    const handleSaveClick = () => {
        onSave({
            name: title,
            script: editedScript,
            timestampedOriginal: cachedTimestampedScript,
            imagePrompt: imagePrompt,
            videoPrompt: videoPrompt
        });

        setShowSaveToast(true);
        setTimeout(() => setShowSaveToast(false), 3000);
    };

    const handleCopy = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
    };

    // Welcome State (if nothing is selected)
    if (!originalData && !initialData) {
        return (
            <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
                <div style={{ textAlign: 'center', maxWidth: '440px', padding: '40px', background: 'white', borderRadius: '24px', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }}>
                    <div className="nav-logo" style={{ margin: '0 auto 24px', width: '64px', height: '64px' }}><Bolt size={32} fill="white" /></div>
                    <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', marginBottom: '12px' }}>Welcome to Rewriter Studio</h2>
                    <p style={{ color: '#64748b', marginBottom: '32px', lineHeight: '1.6' }}>The ultimate tool for TikTok Affiliates. Transcribe, rewrite, and generate visual prompts in seconds.</p>

                    <button className="btn-new" onClick={onNewClick} style={{ padding: '14px 28px', fontSize: '15px', width: '100%', borderRadius: '12px', background: '#111827', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer' }}>
                        <Sparkles size={20} />
                        Get Started (New Video)
                    </button>

                    {rewrites && rewrites.length > 0 && (
                        <div style={{ marginTop: '40px', textAlign: 'left' }}>
                            <p style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '16px', letterSpacing: '0.05em' }}>Jump back in</p>
                            <div style={{ display: 'grid', gap: '10px' }}>
                                {rewrites.slice(0, 3).map(r => (
                                    <div key={r.id} onClick={() => onEdit(r.id)} style={{ padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}>
                                        <div style={{ width: 40, height: 40, borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <VideoIcon size={18} color="#6366f1" />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontSize: '13px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</p>
                                            <p style={{ fontSize: '11px', color: '#64748b' }}>{r.date}</p>
                                        </div>
                                        <ChevronRight size={16} color="#94a3b8" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="studio-layout flex-1 flex flex-col min-w-0">
            {/* ICON SIDEBAR REMOVED - NOW IN App.jsx */}


            {/* --- RIGHT PANEL (Global Header + Content) --- */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* --- GLOBAL HEADER --- */}
                <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 glass-effect z-10 sticky top-0 backdrop-blur-md flex-shrink-0">
                    <div className="max-w-7xl mx-auto h-full flex items-center justify-between px-6">
                        <div className="flex items-center gap-4 flex-1">
                            <FileText size={18} className="text-slate-400" />
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="text-sm font-bold bg-transparent text-slate-900 dark:text-white w-full max-w-sm outline-none px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            />
                            <div className="status-badge">Drafting</div>
                        </div>
                        <div className="editor-actions">
                            <button className="px-4 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Share</button>
                            <button
                                onClick={handleSaveClick}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-colors shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                            >
                                <Save size={14} /> Save & Export
                            </button>
                        </div>
                    </div>
                </header>

                {/* --- MAIN CONTENT AREA WRAPPER --- */}
                <div className="flex-1 overflow-hidden">
                    {/* Telefon: senarai fail duduk di ATAS editor (setinggi 30vh),
                        bukan di sebelah kiri — 256px daripada 375px akan
                        meninggalkan editor selebar ~120px. */}
                    <div className="max-w-7xl mx-auto h-full flex flex-col md:flex-row overflow-hidden">
                        {/* --- FILES SIDEBAR (MATCHING EXTENSION LAYOUT) --- */}
                        <aside className="w-full md:w-64 max-h-[30vh] md:max-h-none border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white/50 dark:bg-slate-900/50 flex-shrink-0">
                            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-shrink-0 h-14">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Recent Files</span>
                                </div>
                                <div className="flex gap-1">
                                    <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400">
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                {/* All Files in their original order */}
                                {rewrites && rewrites.map(rewrite => (
                                    <div
                                        key={rewrite.id}
                                        className={`p-3 rounded-xl border transition-colors cursor-pointer group ${rewrite.id === activeId
                                            ? 'bg-white dark:bg-slate-800 border-primary/30 shadow-sm ring-1 ring-primary/20'
                                            : 'bg-white dark:bg-slate-800/50 border-slate-100 dark:border-slate-800 hover:border-slate-300'
                                            }`}
                                        onClick={() => onEdit(rewrite.id)}
                                    >
                                        <div className="flex gap-3">
                                            <div className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 dark:bg-slate-800 ${rewrite.id !== activeId ? 'opacity-80' : ''}`}>
                                                <img
                                                    src={`https://picsum.photos/seed/${rewrite.id}/100/100`}
                                                    alt="thumb"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1">
                                                    <h3 className={`text-xs font-bold truncate line-clamp-2 ${rewrite.id === activeId ? 'text-slate-800 dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'}`}>
                                                        {rewrite.name}
                                                    </h3>
                                                    {rewrite.id !== activeId && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); onDelete(rewrite.id); }}
                                                            className="text-slate-400 hover:text-rose-500 transition-colors"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>

                                                {rewrite.id === activeId ? (
                                                    <div className="mt-2 flex items-center gap-1 text-[10px] font-bold text-emerald-500">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                                        Editing Now
                                                    </div>
                                                ) : (
                                                    <p className="text-[10px] text-slate-400 mt-1">{rewrite.date}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 bg-slate-100/50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
                                <button
                                    className="w-full py-2.5 bg-slate-900 dark:bg-slate-800 border border-transparent rounded-lg text-xs font-bold text-white hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                                    onClick={onNewClick}
                                >
                                    <RefreshCw size={14} /> Transcribe New
                                </button>
                            </div>
                        </aside>

                        {/* --- MAIN EDITOR --- */}
                        <main className="flex-1 flex flex-col bg-white dark:bg-slate-900 overflow-hidden relative">
                            <div className="scroll-container">
                                <div className="max-width-limit">

                                    {/* Segment Card: FULL SCRIPT */}
                                    <div className="segment-card">
                                        <div className="segment-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="label-pill label-hook">Script Base</span>
                                                <span className="section-time">TikTok Viral Structure</span>
                                            </div>
                                            <MoreHorizontal size={18} color="#94a3b8" />
                                        </div>
                                        <div className="input-container">
                                            <p className="input-heading">Rewritten Script (All Segments)</p>
                                            <textarea
                                                className="script-textarea"
                                                rows={12}
                                                value={editedScript}
                                                onChange={(e) => setEditedScript(e.target.value)}
                                                placeholder="Enter script or click 'Rewrite' below..."
                                                style={{ background: isRewriting ? '#f8fafc' : 'transparent' }}
                                                disabled={isRewriting}
                                            />
                                            {isRewriting && (
                                                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#6366f1', fontSize: '13px', fontWeight: 600 }}>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    AI is rewriting your TikTok script...
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Segment Card: Visual Guidelines */}
                                    <div className="segment-card" style={{ borderLeft: '4px solid #6366f1' }}>
                                        <div className="segment-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="label-pill label-body">AI Scene Direction</span>
                                            </div>
                                            <Sparkles size={18} color="#6366f1" />
                                        </div>

                                        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>AI-generated prompts for scene visuals and B-roll clips.</p>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <div className="prompt-container">
                                                <div className="prompt-header-row">
                                                    <div className="prompt-label">
                                                        <ImageIcon size={16} />
                                                        <span>Image Prompt</span>
                                                    </div>
                                                    <div className="copy-link" onClick={() => handleCopy(imagePrompt)}>
                                                        <Copy size={12} /> Copy
                                                    </div>
                                                </div>
                                                <textarea
                                                    className="prompt-content"
                                                    style={{ width: '100%', background: 'transparent', border: 'none', resize: 'none', outline: 'none' }}
                                                    value={imagePrompt}
                                                    onChange={(e) => setImagePrompt(e.target.value)}
                                                    rows={3}
                                                    placeholder="Image prompts for Midjourney / Leonardo..."
                                                />
                                            </div>

                                            <div className="prompt-container" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
                                                <div className="prompt-header-row">
                                                    <div className="prompt-label" style={{ color: '#475569' }}>
                                                        <VideoIcon size={16} />
                                                        <span>Video B-Roll direction</span>
                                                    </div>
                                                    <div className="copy-link" style={{ color: '#64748b' }} onClick={() => handleCopy(videoPrompt)}>
                                                        <Copy size={12} /> Copy
                                                    </div>
                                                </div>
                                                <textarea
                                                    className="prompt-content"
                                                    style={{ width: '100%', background: 'transparent', border: 'none', resize: 'none', outline: 'none', color: '#475569' }}
                                                    value={videoPrompt}
                                                    onChange={(e) => setVideoPrompt(e.target.value)}
                                                    rows={3}
                                                    placeholder="Scene directions for CapCut / Premiere..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>

                        {/* --- RIGHT TOOLS PANEL --- */}
                        <aside className="tools-panel border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-y-auto">
                            <div className="video-container">
                                {(originalData?.videoUrl || initialData?.videoUrl) ? (
                                    <video
                                        key={originalData?.videoUrl || initialData?.videoUrl}
                                        ref={videoRef}
                                        src={originalData?.videoUrl || initialData?.videoUrl}
                                        className="video-preview"
                                        controls
                                        preload="auto"
                                        style={{ opacity: 1 }}
                                    />
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: '16px' }}>
                                        <VideoIcon size={48} />
                                        <p style={{ fontSize: '13px' }}>Original Video</p>
                                    </div>
                                )}
                            </div>

                            <div className="structure-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="form-label">Viral Structure</span>
                                    <span className="slider-val">Hook • Body • CTA</span>
                                </div>
                                <div className="timeline-bar"></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }}></div>
                                        <span style={{ fontSize: '10px', color: '#64748b' }}>Hook</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }}></div>
                                        <span style={{ fontSize: '10px', color: '#64748b' }}>Body</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }}></div>
                                        <span style={{ fontSize: '10px', color: '#64748b' }}>CTA</span>
                                    </div>
                                </div>
                            </div>

                            <div className="refinement-card">
                                <h3>
                                    <Bolt size={18} color="#6366f1" />
                                    AI Settings
                                </h3>
                                <div className="form-group">
                                    <label className="form-label">Voice / Tone Style</label>
                                    <select
                                        className="select-input"
                                        value={selectedStyle}
                                        onChange={(e) => setSelectedStyle(e.target.value)}
                                    >
                                        <option>Aggressive & Energetic</option>
                                        <option>Educational & Calm</option>
                                        <option>Creative & Viral</option>
                                        <option>Salesy & Persuasive</option>
                                        <option>Storyteller Mode</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Target Word Count</label>
                                    <div className="slider-row">
                                        <input
                                            type="range"
                                            className="slider-input"
                                            min="50" max="600"
                                            value={targetWordCount}
                                            onChange={(e) => setTargetWordCount(e.target.value)}
                                        />
                                        <span className="slider-val">~{targetWordCount}</span>
                                    </div>
                                </div>
                                <button
                                    className="btn-rewrite-all"
                                    onClick={() => handleRewrite(selectedStyle)}
                                    disabled={isRewriting}
                                >
                                    {isRewriting ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
                                    {isRewriting ? 'Processing...' : 'Rewrite Script'}
                                </button>
                                <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '12px', textAlign: 'center' }}>
                                    AI will rewrite the content based on your selected tone and length settings.
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>

                {/* --- TOAST NOTIFICATION --- */}
                {showSaveToast && (
                    <div style={{
                        position: 'fixed',
                        bottom: '24px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#111827',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        zIndex: 1000,
                        animation: 'slideUp 0.3s ease-out'
                    }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Save size={10} color="white" />
                        </div>
                        Changes saved successfully!
                    </div>
                )}
            </div>
        </div>
    );
};

export default RewriteView;
